import {
  AsyncPropSelector,
  HeaderTag,
  Icon,
  getTranslatedText,
  BackgroundIcon,
  AutoCompleteSearchInput
} from '@nile/components';
import {
  DropdownIndicator,
  IndicatorSeparator,
  formatGroupLabel,
  formatOptionLabel
} from '@nile/components/src/Pebbles/select';
import {
  Sheet,
  Button,
  ButtonGroup,
  Heading,
  TextField
} from '@superops/ui-design-system';
import { Search } from '@styled-icons/boxicons-regular/Search';
import dayjs from 'dayjs';
import { cloneDeep, includes } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import AsyncSelect from 'react-select/async';
import styled from 'styled-components';
import { FormattedMessage, injectIntl } from 'react-intl';
import Select from 'react-select';
import envConfig from '@nile/home/src/Config/env';
import { SVGIcon } from '@superops/super-icons';

const noop = () => {};

function SearchFilter(props) {
  const {
    intl,
    personalization,
    updateCondition,
    currentViewConditions,
    allowedProps,
    entity,
    getDefaultValue,
    getDynamicQuery,
    checkDynamicQuery,
    generateOptions,
    getMethodName,
    getKeyName,
    getAsyncOptions,
    fetchValues,
    getVariables,
    getDateOperators
  } = props;

  const [sheetActive, setSheetActive] = useState(false);
  const toggleSheetActive = useCallback(
    () => setSheetActive((active) => !active),
    []
  );
  const handleDone = (condition) => {
    updateCondition(condition);
    setSheetActive(false);
  };
  const properties = personalization
    ? typeof personalization === 'string'
      ? JSON.parse(personalization)
      : personalization
    : [];
  const getProperties = (ps) => {
    if (props.getFilterProperties) {
      const allowedProperties = props.getFilterProperties();
      return allowedProperties;
    }

    let allProps = [];
    ps.forEach((el) => {
      switch (el.name) {
        default:
          allProps.push(el);
      }
    });
    if (allowedProps && allowedProps.length > 0) {
      allProps = allProps.filter((el) => {
        return allowedProps.indexOf(el.name) > -1;
      });
    }
    return allProps;
  };
  const allProperties = getProperties(properties);
  return (
    <div className="ml8">
      <Button
        id="searchFilter"
        customClassName=""
        onClick={toggleSheetActive}
        icon={<SVGIcon name="Filter" />}
      >
        <span className="fx actr">
          <div className="flex center ml8">
            <FormattedMessage id="ticket.search.filter" />
            {currentViewConditions &&
            currentViewConditions?.operands?.length > 0 ? (
              <CountBulb>{currentViewConditions?.operands?.length}</CountBulb>
            ) : (
              ''
            )}
          </div>
        </span>
      </Button>
      <Sheet open={sheetActive} onClose={toggleSheetActive}>
        <FilterSection
          intl={intl}
          handleDone={handleDone}
          entity={entity}
          handleClose={toggleSheetActive}
          properties={allProperties}
          currentViewConditions={currentViewConditions}
          getDefaultValue={getDefaultValue}
          getDynamicQuery={getDynamicQuery}
          checkDynamicQuery={checkDynamicQuery}
          generateOptions={generateOptions}
          getMethodName={getMethodName}
          getKeyName={getKeyName}
          getAsyncOptions={getAsyncOptions}
          fetchValues={fetchValues}
          getVariables={getVariables || noop}
          getDateOperators={getDateOperators || noop}
        />
      </Sheet>
    </div>
  );
}

function FilterSection(props) {
  const {
    intl,
    meta,
    handleDone,
    handleClose,
    properties,
    currentViewConditions,
    entity,
    getDefaultValue,
    getDynamicQuery,
    checkDynamicQuery,
    generateOptions,
    getAsyncOptions,
    fetchValues,
    getVariables,
    getDateOperators
  } = props;
  const [condition, setCondition] = useState({
    joinOperator: 'AND',
    operands: []
  });
  const [filterExpression, setFilterExpression] = useState('');
  useEffect(() => {
    setCondition(currentViewConditions || condition);
  }, [JSON.stringify(currentViewConditions)]);

  const getValue = (f, val) => {
    switch (f.dataType) {
      case 'date':
      case 'datetime':
        return val.key;
      case 'boolean':
        return val ? val.value : null;
      default:
        break;
    }
    switch (f.filterType) {
      case 'text':
      case 'numeric':
      case 'decimal':
      case 'paragraph':
      case 'read_only':
        return val || null;
      default:
        return val ? val.map((el) => el.value) : [];
    }
  };

  const getOperator = (f) => {
    switch (f.dataType) {
      case 'date':
      case 'datetime':
        return 'on';
      case 'boolean':
        return 'is';
      default:
        break;
    }
    switch (f.filterType !== 'select') {
      case 'text':
      case 'numeric':
      case 'decimal':
      case 'paragraph':
      case 'read_only':
        return 'contains';
      default:
        return 'includes';
    }
  };

  const updateSelection = (f, val) => {
    const operands = cloneDeep(condition.operands);
    const ruleIdx = operands.findIndex((el) =>
      f.dataType === 'entity'
        ? el.attribute === `${f.name}.name`
        : el.attribute === f.name
    );
    if (ruleIdx > -1) {
      if (val?.hasOwnProperty('key')) {
        operands[ruleIdx] = {
          ...operands[ruleIdx],
          value: getValue(f, val)
        };
      } else if (val?.length) {
        operands[ruleIdx] = {
          ...operands[ruleIdx],
          value: getValue(f, val)
        };
      } else if (typeof val?.value === 'boolean') {
        operands[ruleIdx] = {
          ...operands[ruleIdx],
          value: getValue(f, val)
        };
      } else {
        operands.splice(ruleIdx, 1);
      }
    } else {
      operands.push({
        attribute: f.dataType === 'entity' ? `${f.name}.name` : f.name,
        operator: getOperator(f),
        value: getValue(f, val)
      });
    }
    setCondition({ ...condition, operands: operands });
  };

  const handleClear = () => {
    setCondition({
      joinOperator: 'AND',
      operands: []
    });
  };

  const getComponent = (property) => {
    const defaultValue = getDefaultValue(property, condition);
    const isDynamicQuery = checkDynamicQuery(property);
    const dynamicQuery = isDynamicQuery
      ? getDynamicQuery.bind(this, property)
      : null;
    if (filterExpression) {
      if (
        property.label &&
        property.label.toLowerCase().indexOf(filterExpression) === -1
      )
        return '';
    }
    if (property.autoComplete) {
      const loadOptions = (inputValue, callback) => {
        if (inputValue.length < 2) return callback([]);
        const asyncOptions = getAsyncOptions(property);
        fetchValues(asyncOptions.client, asyncOptions.query, {
          inputValue,
          property
        }).then((res) => {
          return callback(res);
        });
      };
      return (
        <SearchField>
          <Label>{property.label}</Label>
          <DropdownWrap>
            <AsyncSelect
              isMulti
              cacheOptions
              classNamePrefix="value_selector"
              loadOptions={loadOptions}
              value={defaultValue}
              onChange={updateSelection.bind(this, property)}
              components={{ IndicatorSeparator, DropdownIndicator }}
              closeMenuOnScroll={(e) => {
                return (
                  e.target &&
                  e.target.classList &&
                  !(e.target.classList.value.indexOf('menu-list') > -1)
                );
              }}
              name="valueselector"
              menuPortalTarget={document.body}
              menuPlacement="auto"
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                menu: (base) => ({ ...base, zIndex: 9999 })
              }}
              noOptionsMessage={(params) => {
                return params?.inputValue?.length < 2
                  ? 'Type 2 or more characters to search'
                  : 'No options';
              }}
            />
          </DropdownWrap>
        </SearchField>
      );
    }
    if (property.isCustom) {
      if (
        property.dataType === 'string' &&
        includes(['text', 'paragraph'], property.filterType)
      ) {
        return (
          <SearchField>
            <TextField
              label={property.label}
              placeholder={getTranslatedText(
                intl,
                'ticket.new.columns.contained.pl'
              )}
              value={defaultValue}
              onChange={updateSelection.bind(this, property)}
            />
          </SearchField>
        );
      }
      if (property.dataType === 'number') {
        return (
          <SearchField>
            <TextField
              label={property.label}
              placeholder={getTranslatedText(
                intl,
                'ticket.new.filter.entername'
              )}
              value={defaultValue}
              onChange={updateSelection.bind(this, property)}
              type="number"
            />
          </SearchField>
        );
      }
      if (includes(['date', 'datetime'], property.dataType)) {
        const options = getDateOperators();
        return (
          <SearchField>
            <Label>{property.label}</Label>
            <Select
              className="propSelector"
              key={defaultValue}
              classNamePrefix="value_selector"
              formatGroupLabel={formatGroupLabel}
              formatOptionLabel={formatOptionLabel}
              value={options.find((el) => el.key === defaultValue)}
              components={{ IndicatorSeparator, DropdownIndicator }}
              onChange={updateSelection.bind(this, property)}
              options={options}
              placeholder={getTranslatedText(
                intl,
                'admin.slaPolicy.chooseValue'
              )}
              menuPortalTarget={document.body}
              menuPlacement="auto"
              // closeMenuOnScroll={(e) => {
              //   e.stopPropagation();
              //   e.preventDefault();
              //   return (
              //     e.target &&
              //     e.target.classList &&
              //     !(e.target.classList.value.indexOf('menu-list') > -1)
              //   );
              // }}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            />
          </SearchField>
        );
      }
      return (
        <SearchField>
          <AsyncPropSelector
            defaultValue={defaultValue}
            updateSelection={updateSelection}
            field={property}
            isDynamicQuery={isDynamicQuery}
            dynamicQuery={dynamicQuery}
            generateOptions={generateOptions}
            isMulti
            entity={entity || 'Ticket'}
            variables={getVariables(property)}
          />
        </SearchField>
      );
    }
    if (
      property.dataType === 'string' &&
      !property.isCustom &&
      includes(['text', 'read_only'], property.filterType)
    ) {
      return (
        <SearchField>
          <TextField
            label={property.label}
            placeholder={getTranslatedText(intl, 'ticket.new.filter.entername')}
            value={defaultValue}
            onChange={updateSelection.bind(this, property)}
          />
        </SearchField>
      );
    }
    if (property.dataType === 'string' && !property.isCustom) {
      return (
        <SearchField>
          <AsyncPropSelector
            defaultValue={defaultValue}
            updateSelection={updateSelection}
            field={property}
            isDynamicQuery={isDynamicQuery}
            dynamicQuery={dynamicQuery}
            generateOptions={generateOptions}
            isMulti
            entity={entity || 'Ticket'}
          />
        </SearchField>
      );
    }
    if (property.dataType === 'boolean') {
      return (
        <SearchField>
          <AsyncPropSelector
            defaultValue={defaultValue}
            updateSelection={updateSelection}
            field={property}
            isDynamicQuery={isDynamicQuery}
            dynamicQuery={dynamicQuery}
            generateOptions={generateOptions}
            entity={entity || 'Ticket'}
            variables={getVariables(property)}
          />
        </SearchField>
      );
    }
    if (property.dataType === 'number') {
      return (
        <SearchField>
          <TextField
            label={property.label}
            placeholder={getTranslatedText(intl, 'ticket.new.filter.entername')}
            value={defaultValue}
            onChange={updateSelection.bind(this, property)}
            type="number"
          />
        </SearchField>
      );
    }
    if (property.filterType === 'select') {
      return (
        <SearchField>
          <AsyncPropSelector
            defaultValue={defaultValue}
            updateSelection={updateSelection}
            field={property}
            isDynamicQuery={isDynamicQuery}
            dynamicQuery={dynamicQuery}
            generateOptions={generateOptions}
            isMulti
            entity={entity || 'Ticket'}
            variables={getVariables(property)}
          />
        </SearchField>
      );
    }
    if (includes(['date', 'datetime'], property.dataType)) {
      const options = getDateOperators();
      return (
        <SearchField>
          <Label>{property.label}</Label>
          <Select
            className="propSelector"
            key={defaultValue}
            classNamePrefix="value_selector"
            formatGroupLabel={formatGroupLabel}
            formatOptionLabel={formatOptionLabel}
            value={options.find((el) => el.key === defaultValue)}
            components={{ IndicatorSeparator, DropdownIndicator }}
            onChange={updateSelection.bind(this, property)}
            options={options}
            placeholder={getTranslatedText(intl, 'admin.slaPolicy.chooseValue')}
            menuPortalTarget={document.body}
            menuPlacement="auto"
            // closeMenuOnScroll={(e) => {
            //   e.stopPropagation();
            //   e.preventDefault();
            //   return (
            //     e.target &&
            //     e.target.classList &&
            //     !(e.target.classList.value.indexOf('menu-list') > -1)
            //   );
            // }}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
        </SearchField>
      );
    }
  };

  const handleApply = () => {
    const conditionParams = condition.operands.length ? condition : null;
    handleDone(conditionParams);
  };

  const debouncedFilter = useCallback((value) => {
    setFilterExpression(value.toLowerCase());
  });

  return (
    <FilterWrapper>
      {properties.length > 0 ? (
        <>
          <div className="flex mb25" data-qa="searchFilter">
            <Heading element="h1">
              {meta?.heading || getTranslatedText(intl, 'ticket.search.filter')}
            </Heading>
            <div className="fx mlauto">
              <ButtonGroup>
                <Button plain onClick={handleClear}>
                  <FormattedMessage id="ticket.search.reset" />
                </Button>
                <Button plain onClick={handleClose}>
                  <FormattedMessage id="common.close" />
                </Button>
                <Button primary onClick={handleApply}>
                  <FormattedMessage id="ticket.search.apply" />
                </Button>
              </ButtonGroup>
            </div>
          </div>
          <AutoCompleteSearchInput debouncedFilter={debouncedFilter} />
          <HeaderTag
            title={
              meta?.title ||
              getTranslatedText(intl, 'ticket.search.filtercondn')
            }
            isBgStrip
            isNewLayout
          />
          <PropertyWrapper>
            {properties.map((property) => {
              return getComponent(property);
            })}
          </PropertyWrapper>
        </>
      ) : (
        <div>
          <FormattedMessage id="ticket.details.search.msg" />
        </div>
      )}
    </FilterWrapper>
  );
}

const FilterWrapper = styled.div`
  width: 30vw;
  background: #fff;
  min-height: 100vh;
  padding: 20px;
  height: 100%;
  .Polaris-ButtonGroup {
    margin-left: auto;
  }
  .value_selector__control {
    border: none !important;
    border-bottom: 1px solid rgba(151, 151, 151, 0.3) !important;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    background: #ffffff;
    cursor: pointer !important;
  }
  .value_selector_control--is-focused {
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
  }
  .Polaris-Labelled__LabelWrapper {
    margin-bottom: 10px;
  }
  .Polaris-Label__Text {
    font-weight: 400;
    opacity: 1;
  }
  .Search-Input {
    margin-left: 0 !important;
    margin-right: 0 !important;
  }
`;

const DropdownWrap = styled.div`
  width: 98%;
  padding-left: 2px;
`;

const SearchField = styled.div`
  margin-bottom: 10px;
`;

const PropertyWrapper = styled.div`
  overflow: auto;
  height: calc(100% - 190px);
  .m10 {
    margin: 10px;
  }
`;

const Label = styled.div`
  color: #616263;
  text-align: left;
  margin-top: 10px;
`;

const CountBulb = styled.div`
  display flex;
  align-items: center;
  color: #fff;
  border-radius: 4px;
  background-color: #333;
  margin-left: 8px;
  min-width: 20px;
  justify-content: center;
  text-align: center;
  font-size: 12px;
`;

export default injectIntl(SearchFilter);
